<script>
/**
 * @component AlertaCriticaCard
 * @description Card individual del bloque "Alertas críticas" del tablero de
 *              seguimiento rediseñado (UC-REG-065). Solo se renderiza para
 *              alertas que cumplen los criterios críticos definidos en
 *              `services/monitoring.js#isCriticalAlert`.
 *
 *              Border-left con color del semáforo + snippet de la quote del
 *              paciente o el valor crítico que disparó la alerta. Botones
 *              "Atender" (lleva al dashboard del paciente con anchor a la
 *              conversación) y "Marcar visto" (mark as seen sin abrir).
 *
 * @example <AlertaCriticaCard :alert="alert" @atender="onAtender" @marcar="onMarcar" />
 */
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";

export default {
  name: "AlertaCriticaCard",

  props: {
    /**
     * @description Objeto de alerta. Shape esperado (de alertsService.getAllAlerts
     *              extendido con info de healthConversation):
     *              {
     *                _id, patientId, patientName, programLabel,
     *                type: 'health-conversation'|'vital'|'checkin'|'medication',
     *                taskName, completedMeasurement,
     *                score, signals[], quote, value,
     *                completedAt, hasRisk
     *              }
     */
    alert: {
      type: Object,
      required: true,
    },
  },

  computed: {
    /**
     * @description Clase de border-left según semáforo.
     */
    semaphoreClass() {
      const s = this.alert.completedMeasurement || "red";
      return `alerta-critica--${s}`;
    },
    /**
     * @description Etiqueta + ícono del tipo de tarea.
     */
    typeLabel() {
      const map = {
        "health-conversation": { icon: "💬", label: "Conversación de salud" },
        vital: { icon: "🩺", label: "Vital" },
        checkin: { icon: "✓", label: "Check-in" },
        medication: { icon: "💊", label: "Medicación" },
      };
      return map[this.alert.type] || { icon: "📋", label: this.alert.type };
    },
    /**
     * @description Texto principal de métrica (score o valor crítico).
     */
    metric() {
      if (this.alert.score != null) {
        const turns = this.alert.turnsUsed
          ? ` · ${this.alert.turnsUsed} turnos`
          : "";
        return `Score ${this.alert.score}/10${turns}`;
      }
      if (this.alert.value) return this.alert.value;
      return this.alert.taskName || "—";
    },
    /**
     * @description Quote del paciente o snippet de signal de alta severidad.
     *              Trunca a 90 chars para no inflar la card.
     */
    quoteText() {
      const q = this.alert.quote || this.alert.signalSnippet || "";
      if (!q) return "";
      return q.length > 90 ? `${q.slice(0, 90)}...` : q;
    },
    /**
     * @description "hace Xh" o "hace Yd" relativo en es-AR.
     */
    relativeTime() {
      if (!this.alert.completedAt) return "";
      return formatDistanceToNow(new Date(this.alert.completedAt), {
        locale: es,
        addSuffix: true,
      });
    },
  },

  methods: {
    /**
     * @description Click en card o botón "Atender" → emit para que el padre
     *              navegue al dashboard del paciente.
     */
    onAtender() {
      this.$emit("atender", this.alert);
    },
    /**
     * @description Marca la alerta como vista sin abrirla.
     */
    onMarcar() {
      this.$emit("marcar", this.alert);
    },
  },
};
</script>

<template lang="pug">
  .alerta-critica(:class="semaphoreClass" @click="onAtender" role="button" tabindex="0")
    .alerta-critica__header
      .alerta-critica__patient
        span.alerta-critica__semaphore(:class="`semaphore--${alert.completedMeasurement || 'red'}`")
        span.alerta-critica__name {{ alert.patientName }}
      span.alerta-critica__time {{ relativeTime }}

    .alerta-critica__program(v-if="alert.programLabel") {{ alert.programLabel }}

    .alerta-critica__type
      span.alerta-critica__type-icon {{ typeLabel.icon }}
      span {{ typeLabel.label }}

    .alerta-critica__metric {{ metric }}

    .alerta-critica__risk(v-if="alert.hasRisk")
      span ⚠ Riesgo detectado

    .alerta-critica__quote(v-if="quoteText") "{{ quoteText }}"

    .alerta-critica__actions(@click.stop)
      el-button(type="primary" size="small" @click="onAtender") Atender
      el-button(size="small" @click="onMarcar") ✓ Marcar
</template>

<style lang="scss" scoped>
.alerta-critica {
  background: white;
  border: 1px solid #e5e7eb;
  border-left: 4px solid;
  border-radius: 8px;
  padding: 14px 16px;
  cursor: pointer;
  font-family: "Plus Jakarta Sans", sans-serif;
  transition: box-shadow 0.15s ease, transform 0.1s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(15, 30, 54, 0.08);
  }

  &--red {
    border-left-color: #b3261e;
  }
  &--yellow {
    border-left-color: #b07c0a;
  }
  &--green {
    border-left-color: #1f8a4c;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }
  &__patient {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  &__semaphore {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: currentColor;
    &.semaphore--red {
      color: #b3261e;
    }
    &.semaphore--yellow {
      color: #b07c0a;
    }
    &.semaphore--green {
      color: #1f8a4c;
    }
  }
  &__name {
    font-weight: 700;
    color: #0f1e36;
    font-size: 14px;
  }
  &__time {
    font-size: 11px;
    color: #9ca3af;
  }
  &__program {
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 12px;
  }
  &__type {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 600;
    color: #111827;
    margin-bottom: 4px;
  }
  &__metric {
    font-size: 13px;
    color: #111827;
    margin-bottom: 8px;
    font-weight: 600;
  }
  &__risk {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: #b3261e;
    font-weight: 700;
    padding: 2px 8px;
    background: #fbe9e7;
    border-radius: 4px;
    margin-bottom: 8px;
  }
  &__quote {
    background: #f9fafb;
    border-left: 2px solid #e5e7eb;
    padding: 6px 10px;
    font-size: 12px;
    color: #6b7280;
    font-style: italic;
    margin: 8px 0 12px;
    border-radius: 0 4px 4px 0;
  }
  &__actions {
    display: flex;
    gap: 8px;
    margin-top: 4px;
  }
}
</style>
